import { useMemo, useState } from "react";
import type { Article } from "@/types/portal";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/site";
import { SectionHead, CategoryBadge, catName } from "./bits";
import ArticleModal from "./ArticleModal";

const chipActive =
  "bg-primary-600 text-background-50 border-primary-600";
const chipIdle =
  "bg-background-50 text-foreground-600 border-background-200 hover:border-primary-300 hover:text-primary-700";

export default function LatestFeed({
  activeCat,
  onCatChange,
}: {
  activeCat: string;
  onCatChange: (slug: string) => void;
}) {
  const [keyword, setKeyword] = useState("");
  const [selected, setSelected] = useState<Article | null>(null);

  const list = useMemo(() => {
    let arr = activeCat === "all" ? articles : articles.filter((a) => a.cat === activeCat);
    const kw = keyword.trim().toLowerCase();
    if (kw) {
      arr = arr.filter(
        (a) =>
          a.title.toLowerCase().includes(kw) ||
          a.excerpt.toLowerCase().includes(kw) ||
          a.tag.toLowerCase().includes(kw)
      );
    }
    return arr;
  }, [activeCat, keyword]);

  const feature = !keyword && activeCat === "all" ? list.find((a) => a.featured) : undefined;
  const rest = feature ? list.filter((a) => a.id !== feature.id) : list;

  return (
    <section id="feed" className="scroll-mt-24">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <SectionHead
          eyebrow="Latest"
          title="最新瓜闻 · 全网滚动"
          desc="顶流塌房预警、税务问题爆料、合同纠纷内幕与行业潜规则曝光，按栏目实时归类。输入关键词或切换栏目，即可定向吃瓜。"
          action={
            <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2 focus-within:border-primary-300">
              <i className="ri-search-line text-foreground-400" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索标题 / 栏目关键词…"
                aria-label="搜索瓜闻"
                className="w-44 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400 md:w-56"
              />
              {keyword ? (
                <button
                  type="button"
                  onClick={() => setKeyword("")}
                  aria-label="清空搜索"
                  className="text-foreground-400 hover:text-primary-600"
                >
                  <i className="ri-close-circle-fill" />
                </button>
              ) : null}
            </div>
          }
        />

        {/* chips */}
        <div className="scrollbar-none -mx-4 mb-8 flex gap-2 overflow-x-auto px-4 md:mx-0 md:flex-wrap md:overflow-visible md:px-0">
          <button
            type="button"
            onClick={() => onCatChange("all")}
            className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition ${
              activeCat === "all" ? chipActive : chipIdle
            }`}
          >
            全部瓜闻
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => onCatChange(c.slug)}
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition ${
                activeCat === c.slug ? chipActive : chipIdle
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* featured */}
        {feature ? (
          <button
            type="button"
            onClick={() => setSelected(feature)}
            className="group mb-8 grid w-full overflow-hidden rounded-2xl border border-background-200 bg-background-50 text-left lg:grid-cols-2"
          >
            <div className="relative h-60 w-full overflow-hidden lg:h-auto">
              <img
                src={feature.image}
                alt={feature.title}
                className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
              />
              <span className="absolute left-4 top-4 rounded-full bg-primary-600 px-3 py-1 text-xs font-medium text-background-50">
                头条推荐
              </span>
            </div>
            <div className="flex flex-col justify-center p-6 md:p-8">
              <CategoryBadge slug={feature.cat} />
              <h3 className="mt-4 font-heading text-xl font-bold leading-snug text-foreground-950 transition group-hover:text-primary-700 md:text-2xl">
                {feature.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                {feature.excerpt}
              </p>
              <div className="mt-5 flex items-center gap-4 text-xs text-foreground-400">
                <span>{feature.date} {feature.time}</span>
                <span>{feature.reads} 阅读</span>
                <span className="font-medium text-primary-600">{feature.source}</span>
              </div>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary-600">
                展开完整报道
                <i className="ri-arrow-right-line transition group-hover:translate-x-1" />
              </span>
            </div>
          </button>
        ) : null}

        {/* grid */}
        {rest.length ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((a) => (
              <article
                key={a.id}
                onClick={() => setSelected(a)}
                className="group cursor-pointer overflow-hidden rounded-xl border border-background-200 bg-background-50 transition hover:-translate-y-1 hover:border-primary-200"
              >
                <div className="relative h-44 w-full overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.title}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3">
                    <span className="rounded-full bg-background-50/90 px-2.5 py-1 text-[11px] font-medium text-foreground-700 backdrop-blur">
                      {a.tag}
                    </span>
                  </span>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2">
                    <CategoryBadge slug={a.cat} />
                    <span className="ml-auto text-xs text-foreground-300">{a.date}</span>
                  </div>
                  <h3 className="mt-3 font-heading text-[15px] font-bold leading-snug text-foreground-950 transition group-hover:text-primary-700 md:text-base">
                    {a.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                    {a.excerpt}
                  </p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3 text-xs text-foreground-400">
                    <span>{a.reads} 阅读</span>
                    <span className="inline-flex items-center gap-1 text-primary-600">
                      阅读详情
                      <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center rounded-xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-300">
              <i className="ri-folder-close-line" />
            </span>
            <p className="mt-4 font-medium text-foreground-700">暂无匹配的瓜闻</p>
            <p className="mt-1 text-sm text-foreground-400">
              {activeCat !== "all" ? `栏目「${catName(activeCat)}」` : "当前搜索条件"}下还没有内容，换个关键词试试
            </p>
            <button
              type="button"
              onClick={() => {
                onCatChange("all");
                setKeyword("");
              }}
              className="mt-5 whitespace-nowrap rounded-md border border-background-200 px-4 py-2 text-sm text-foreground-700 transition hover:border-primary-300 hover:text-primary-700"
            >
              查看全部瓜闻
            </button>
          </div>
        )}
      </div>

      {selected ? <ArticleModal article={selected} onClose={() => setSelected(null)} /> : null}
    </section>
  );
}